import type { TmsInvoiceDetail, TmsLoadRow } from '@numeo/types-main-web-app'
import { DocumentChips } from './DocumentChips'
import { formatUsd, laneOf, LOAD_STAGE_LABEL, STATUS_GROUP_LABEL } from './format'
import { InvoiceCell } from './threadInvoices'
import { LoadFixActions } from './LoadFixActions'
import { documentActions } from './rowActions'

/** One label and its value in a preview. */
const Fact = ({ label, value }: { label: string; value: string }) => (
    <div className="flex flex-col">
        <dt className="text-[11px] text-foreground-muted">{label}</dt>
        <dd className="text-xs text-foreground">{value}</dd>
    </div>
)

/** An invoice read in full (`tms_invoices.list` with `detail: 'full'`): its facts and the loads on it. */
export const InvoicePreview = ({ invoice }: { invoice: TmsInvoiceDetail }) => (
    <article className="flex flex-col gap-2 rounded-xl bg-default p-3" aria-label={`Invoice ${invoice.invoice_number ?? ''}`.trim()}>
        <header className="flex items-center gap-2 text-sm font-semibold text-foreground">
            Invoice {invoice.invoice_number ?? '(no number)'}
            <span className="ml-auto text-xs font-normal text-foreground-muted">{STATUS_GROUP_LABEL[invoice.status_group] ?? invoice.status_group}</span>
        </header>
        <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Fact label="Broker" value={invoice.broker ?? '—'} />
            <Fact label="Amount due" value={formatUsd(invoice.amount_due_usd)} />
            <Fact label="Invoice date" value={invoice.invoice_date ?? '—'} />
            <Fact label="Due date" value={invoice.due_date ?? '—'} />
        </dl>
        {invoice.lines.length > 0 && (
            <ul className="flex flex-col gap-1 text-xs">
                {invoice.lines.map((line) => (
                    <li key={line.load_number} className="flex justify-between border-t border-border pt-1">
                        <span>#{line.load_number}</span>
                        <span className="tabular-nums">{formatUsd(line.amount_usd)}</span>
                    </li>
                ))}
            </ul>
        )}
    </article>
)

/**
 * One load (`tms_loads.list` for that load), e.g. the load a rate con just created (D16). A delivered one also shows its invoice and
 * documents, and the upload of a missing rate con or POD.
 */
export const LoadPreview = ({ load, onSend }: { load: TmsLoadRow; onSend: (message: string) => void }) => {
    const fixes = load.load_number ? documentActions(load.document_chips) : []
    return (
        <article className="flex flex-col gap-2 rounded-xl bg-default p-3" aria-label={`Load ${load.load_number ?? ''}`.trim()}>
            <header className="flex items-center gap-2 text-sm font-semibold text-foreground">
                Load #{load.load_number ?? '—'}
                <span className="ml-auto text-xs font-normal text-foreground-muted">{LOAD_STAGE_LABEL[load.status] ?? load.status}</span>
            </header>
            <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Fact label="Broker" value={load.broker ?? '—'} />
                <Fact label="Rate" value={formatUsd(load.broker_rate_usd)} />
                <Fact label="Lane" value={laneOf(load.origin, load.destination)} />
                <Fact label="Pickup" value={load.pickup_window_open ? new Date(load.pickup_window_open).toLocaleString() : '—'} />
                {load.status === 'delivered' && (
                    <div className="flex flex-col">
                        <dt className="text-[11px] text-foreground-muted">Invoice</dt>
                        <dd className="text-xs text-foreground">
                            <InvoiceCell row={load} />
                        </dd>
                    </div>
                )}
            </dl>
            <DocumentChips documents={load.document_chips} />
            {load.load_number && fixes.length > 0 && <LoadFixActions loadNumber={load.load_number} loadId={load.load_id} actions={fixes} onSend={onSend} />}
        </article>
    )
}
